/**
 * Шапка карточки байка (§2.2.1): номер, заряд, связь, замок, состояние,
 * расстояние, маршрут.
 *
 * ⚠ ЗАМОК ЗДЕСЬ — ПО HEARTBEAT, А НЕ ПО ОТВЕТУ КОМАНДЫ. Ответ команды
 * («success»/«timeout») не говорит, закрылась ли дужка физически — только
 * что радиообмен прошёл или нет. Правду знает следующий heartbeat замка,
 * поэтому шапка читает `locked` из уже загруженного снимка (парк/задача,
 * `lib/bike-view.ts`), а `bike-lock.tsx` отдельно перепроверяет его после
 * «Закрыть» через явный опрос (`useScoutDevice`), а не переиспользует эту
 * функцию.
 */
import { useEffect, useState, type ReactNode } from 'react'
import type { BikeView } from '@/lib/bike-view'
import { displayNumber } from '@/lib/bike-view'
import { batteryText, lockText, type BikeStatus } from '@/lib/describe-bike'
import { formatDistance, hasPosition, timeAgo } from '@/lib/geo'
import { navUrl } from '@/lib/navigation'
import { StatusChip } from '@/components/bike/status-chip'

function connectionText(view: BikeView): string {
  if (view.runtimeStale === undefined) return 'связь неизвестна'
  const ago = timeAgo(view.lastSeenAt)
  if (view.runtimeStale) return ago ? `не на связи, ${ago}` : 'не на связи'
  return ago ? `на связи, ${ago}` : 'на связи'
}

/** Сколько ждать после свежей точки, пока показываем секунды, а не минуты. */
const GPS_TICK_WINDOW_MS = 60_000

/**
 * «Точка N с назад» — секундная точность, в отличие от `timeAgo` (тот
 * округляет до минут и не годится: свежесть ЭТОЙ точки — весь смысл кнопки
 * «Обновить GPS»). `timeAgo` при этом не трогаем — на него завязаны
 * `connectionText` выше и `geo.test.ts`.
 */
function gpsAgoText(updatedAt: number, now: number): string | null {
  if (updatedAt === 0) return null
  const sec = Math.max(0, Math.round((now - updatedAt) / 1000))
  return sec < 60 ? `точка ${sec} с назад` : `точка ${Math.floor(sec / 60)} мин назад`
}

/**
 * Тикает раз в секунду, пока не прошла минута с последнего опроса, — иначе
 * секунды на экране застыли бы в момент ответа и лгали бы дальше. После
 * минуты смысла в тике нет: `gpsAgoText` сам переходит на минуты, как и
 * весь остальной экран.
 */
function useTickingNow(updatedAt: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (updatedAt === 0 || now - updatedAt >= GPS_TICK_WINDOW_MS) return
    const id = setTimeout(() => {
      setNow(Date.now())
    }, 1000)
    return () => {
      clearTimeout(id)
    }
  }, [updatedAt, now])
  return now
}

export function BikeHeader({
  view,
  status,
  distance,
  action,
  gpsRefresh,
}: {
  view: BikeView
  status: BikeStatus
  /** От скаута до байка. `null` — геопозиция или координаты байка неизвестны. */
  distance: number | null
  /**
   * Действие рядом с номером и бейджем — сегодня это только «Взять» для
   * невзятого задания диспетчера (§2.2.5: «показывается в шапке, а не
   * внизу», чтобы не долистывать до раздела «Задание»). `undefined` —
   * действия нет.
   */
  action?: ReactNode
  /**
   * «Обновить GPS» (§2.2.1) — живой опрос устройства рядом со «связью», не
   * через outbox (как unlock/lock). Опрос и его состояние держит вызывающая
   * страница (`bike-card.tsx`) — здесь только кнопка и текст.
   */
  gpsRefresh: {
    onRefresh: () => void
    /** Идёт опрос — до ~2 мин, живой радиообмен с замком. */
    busy: boolean
    /** `dataUpdatedAt` запроса. 0 — ещё не опрашивали ни разу. */
    updatedAt: number
    /** Текст отказа («устройство не ответило») или `null`, если отказа нет. */
    error: string | null
  }
}) {
  const now = useTickingNow(gpsRefresh.updatedAt)
  const gpsNote = gpsRefresh.error ?? gpsAgoText(gpsRefresh.updatedAt, now)

  return (
    <header className="mb-4">
      {/* Номер — самый крупный текст экрана: это единственное, чем скаут
          сверяет «тот ли это байк», стоя рядом с ним. */}
      <h1 className="font-mono text-5xl font-bold tracking-tight">
        {displayNumber(view) || 'Без номера'}
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusChip status={view.status}>{status.label}</StatusChip>
        <span className="text-sm opacity-70">{batteryText(view)}</span>
        <span className="text-sm opacity-70">{connectionText(view)}</span>
        <span className="text-sm opacity-70">{lockText(view)}</span>
        <button
          onClick={gpsRefresh.onRefresh}
          disabled={gpsRefresh.busy}
          className="min-h-11 rounded-lg border border-white/20 px-3 py-1 text-sm font-medium
            disabled:opacity-40"
        >
          {gpsRefresh.busy ? 'Опрашиваем…' : 'Обновить GPS'}
        </button>
      </div>

      {gpsNote ? (
        <p className={`mt-1 text-xs ${gpsRefresh.error ? 'text-red-300' : 'opacity-60'}`}>
          {gpsNote}
        </p>
      ) : null}

      {action ? <div className="mt-3">{action}</div> : null}

      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-sm opacity-60">
          {distance !== null ? `${formatDistance(distance)} от вас` : 'расстояние неизвестно'}
        </p>
        {hasPosition(view) ? (
          <a
            className="min-h-11 shrink-0 rounded-lg border border-white/20 px-3 py-2 text-sm
              font-medium"
            href={navUrl(view)}
            target="_blank"
            rel="noreferrer"
          >
            Маршрут
          </a>
        ) : null}
      </div>
    </header>
  )
}
